/**
 * AnimatedCounter — number display that scales + flashes on value change.
 * Uses CSS transforms + opacity for GPU-accelerated animation.
 */

import { useEffect, useRef, useState } from 'react';

interface AnimatedCounterProps {
  value: number;
  format: (n: number) => string;
  className?: string;
  /** Color to flash on increase (default: gold) */
  flashColor?: string;
}

export function AnimatedCounter({
  value,
  format,
  className = '',
  flashColor = '#FFD700',
}: AnimatedCounterProps) {
  const prevValue = useRef(value);
  const [animClass, setAnimClass] = useState('');

  useEffect(() => {
    if (value !== prevValue.current) {
      const increased = value > prevValue.current;
      setAnimClass(increased ? 'counter-bump-up' : 'counter-bump-down');
      prevValue.current = value;

      const id = setTimeout(() => setAnimClass(''), 400);
      return () => clearTimeout(id);
    }
  }, [value]);

  return (
    <>
      {/* Inline keyframes — injected once via a hidden <style> tag */}
      <style>{counterKeyframes}</style>
      <span
        className={`inline-block ${className} ${animClass}`}
        style={
          animClass === 'counter-bump-up'
            ? { color: flashColor }
            : undefined
        }
      >
        {format(value)}
      </span>
    </>
  );
}

const counterKeyframes = `
@keyframes counterBumpUp {
  0%   { transform: scale(1);   }
  30%  { transform: scale(1.25); }
  100% { transform: scale(1);   }
}
@keyframes counterBumpDown {
  0%   { transform: scale(1);   }
  30%  { transform: scale(0.85); }
  100% { transform: scale(1);   }
}
.counter-bump-up {
  animation: counterBumpUp 0.4s ease-out;
  transition: color 0.4s ease-out;
}
.counter-bump-down {
  animation: counterBumpDown 0.35s ease-out;
}
`;
